
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route, useParams } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import Partners from "./pages/Partners";
import Leads from "./pages/Leads";
import LeadStatistics from "./pages/LeadStatistics";
import Reports from "./pages/Reports";
import Messages from "./pages/Messages";
import MessageStatistics from "./pages/MessageStatistics";
import LeadDetails from "./pages/LeadDetails";
import AdminLeadDetails from "./pages/AdminLeadDetails";
import PartnerLeadsDashboard from "./pages/PartnerLeadsDashboard";
import PartnersMap from "./pages/PartnersMap";
import Admins from "./pages/Admins";
import SiteSettings from "./pages/SiteSettings";
import AdminLayout from "./components/layouts/AdminLayout";
import AdminLogin from "./pages/AdminLogin";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerPasswordReset from "./pages/PartnerPasswordReset";
import PartnerActivate from "./pages/PartnerActivate";
import EmailTrackingStats from "./pages/EmailTrackingStats";
import AssignmentStats from "./pages/AssignmentStats";
import PartnerReports from "./pages/PartnerReports";
import NewPartner from "./pages/NewPartner";
import EditPartner from "./pages/EditPartner";
import PartnerRegistration from "./pages/PartnerRegistration";
import ThankYou from "./pages/ThankYou";
import PartnerMiniPage from "./pages/PartnerMiniPage";
import AdminChangelog from "./pages/AdminChangelog";
import LeadJourney from "./pages/LeadJourney";
import BillingPage from "./pages/BillingPage";
import EmailTestPage from "./pages/EmailTestPage";
import NewManualLead from "./pages/NewManualLead";
import PartnerDashboardLayout from "./pages/partner/PartnerDashboardLayout";
import PartnerPipelinePage from "./pages/partner/PartnerPipelinePage";
import PartnerLeadsPage from "./pages/partner/PartnerLeadsPage";
import PartnerAnalyticsPage from "./pages/partner/PartnerAnalyticsPage";
import PartnerActivityPage from "./pages/partner/PartnerActivityPage";
import PartnerBillingPage from "./pages/partner/PartnerBillingPage";
import PartnerSettingsPage from "./pages/partner/PartnerSettingsPage";
import MetaLeadHub from "./pages/MetaLeadHub";
import OperationsInbox from "./pages/OperationsInbox";
import PartnerCare from "./pages/PartnerCare";
import Newsletter from "./pages/Newsletter";

const queryClient = new QueryClient();

const LegacyPartnerDashboardRedirect = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  return <Navigate replace to={partnerId ? `/partner/${partnerId}/pipeline` : '/partner/login'} />;
};

const App = () => (
  <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
    <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Index />} />
                  {/* Lead details page remains accessible to all */}
                  <Route path="/lead/:id" element={<LeadDetails />} />
                  {/* Thank you page */}
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/mini/:leadId" element={<PartnerMiniPage />} />
                  <Route path="/mini-preview/:partnerId" element={<PartnerMiniPage />} />
                  {/* Partner routes */}
                  <Route path="/partner/login" element={<PartnerLogin />} />
                  <Route path="/partner/password-reset" element={<PartnerPasswordReset />} />
                  <Route path="/partner/activate" element={<PartnerActivate />} />
                  <Route path="/partner/:partnerId" element={<PartnerDashboardLayout />}>
                    <Route index element={<PartnerPipelinePage />} />
                    <Route path="pipeline" element={<PartnerPipelinePage />} />
                    <Route path="leads" element={<PartnerLeadsPage />} />
                    <Route path="analytics" element={<PartnerAnalyticsPage />} />
                    <Route path="activity" element={<PartnerActivityPage />} />
                    <Route path="billing" element={<PartnerBillingPage />} />
                    <Route path="settings" element={<PartnerSettingsPage />} />
                  </Route>
                  {/* Legacy dashboard route for backward compatibility */}
                  <Route path="/partner/:partnerId/dashboard" element={<LegacyPartnerDashboardRedirect />} />
                  <Route path="/partner/register" element={<PartnerRegistration />} />
                  {/* Admin login page */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  {/* Protected admin routes - AdminLayout already has the context providers */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="partners" element={<Partners />} />
                    <Route path="partners/new" element={<NewPartner />} />
                    <Route path="partners/edit/:id" element={<EditPartner />} />
                    <Route path="partners/map" element={<PartnersMap />} />
                    <Route path="partners/:partnerId/dashboard" element={<PartnerLeadsDashboard />} />
                    <Route path="leads" element={<Leads />} />
                    <Route path="leads/new" element={<NewManualLead />} />
                    <Route path="lead/:id" element={<AdminLeadDetails />} />
                    <Route path="leads/statistics" element={<LeadStatistics />} />
                    <Route path="leads/assignment" element={<AssignmentStats />} />
                    <Route path="leads/meta" element={<MetaLeadHub />} />
                    <Route path="operations" element={<OperationsInbox />} />
                    <Route path="partner-care" element={<PartnerCare />} />
                    <Route path="newsletter" element={<Newsletter />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="reports/partners" element={<PartnerReports />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="messages/statistics" element={<MessageStatistics />} />
                    <Route path="email/statistics" element={<EmailTrackingStats />} />
                    <Route path="settings" element={<SiteSettings />} />
                    <Route path="settings/email-test" element={<EmailTestPage />} />
                    <Route path="admins" element={<Admins />} />
                    <Route path="changelog" element={<AdminChangelog />} />
                    <Route path="journey" element={<LeadJourney />} />
                    <Route path="billing" element={<BillingPage />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
